// ./src/components/features/Artists/EventArtistFilesEditor/EventArtistFilesEditor.tsx
//
// Repeatable editor for a booking's document attachments (contract, rider, stage
// plot, tech spec, invoice…). The FULL desired set is sent on save and diffed on
// `id` server-side, so existing rows keep their id. Mirrors ArtistMembersEditor.
//
// The app's media picker (MediaSelector, via MediaImageField) is the only in-app
// uploader to ms_storage today; it returns an uploadedFileId + url, so on each pick
// we resolve the stored file's fileName / contentType / fileSize via
// UploadedFileService.get to snapshot them onto the row (all optional server-side).

import React, { useCallback } from "react";
import { Stack, Group, ActionIcon, Tooltip, Button, Text, Box, Paper } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  EventArtistFile,
  UploadedFile,
  UploadedFileService,
  ARTIST_FILE_KIND,
  ARTIST_FILE_KIND_VALUES,
  ARTIST_FILE_KIND_LABELS,
} from "@/utils/api";
import { AppIcon, MediaImageField, MediaImageRef, SelectField } from "@/components/generic";

export interface EventArtistFilesEditorProps {
  value: EventArtistFile[];
  onChange: (files: EventArtistFile[]) => void;
  disabled?: boolean;
  error?: string | null;
}

const emptyFile = (): EventArtistFile => ({
  fileKind: ARTIST_FILE_KIND.CONTRACT,
  uploadedFileId: null,
  uploadedFile: null,
  fileName: null,
  contentType: null,
  fileSize: null,
});

export const EventArtistFilesEditor: React.FC<EventArtistFilesEditorProps> = ({
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const { t } = useTranslation(["artists", "common"]);

  const kindOptions = ARTIST_FILE_KIND_VALUES.map((v) => ({
    value: v,
    label: t(`artists:fileKind.${v}`, { defaultValue: ARTIST_FILE_KIND_LABELS[v] }),
  }));

  const updateAt = useCallback(
    (index: number, patch: Partial<EventArtistFile>) => {
      onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    },
    [value, onChange]
  );

  const removeAt = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  const addFile = useCallback(() => {
    onChange([...value, emptyFile()]);
  }, [value, onChange]);

  const handleMedia = useCallback(
    async (index: number, media: MediaImageRef | null) => {
      if (!media?.uploadedFileId) {
        updateAt(index, {
          uploadedFileId: null,
          uploadedFile: null,
          fileName: null,
          contentType: null,
          fileSize: null,
        });

        return;
      }

      // Seed what we know immediately; resolve the stored file's metadata to snapshot it.
      updateAt(index, {
        uploadedFileId: media.uploadedFileId,
        uploadedFile: { id: media.uploadedFileId, fileUrl: media.url ?? null },
      });

      const result = await UploadedFileService.get(media.uploadedFileId);

      if (result.didSucceed()) {
        const file = result.getDataFirst() as UploadedFile | undefined;

        if (file) {
          updateAt(index, {
            fileName: file.originalFilename || file.name || null,
            contentType: file.mimeType || null,
            fileSize: file.sizeBytes ?? null,
          });
        }
      }
    },
    [updateAt]
  );

  return (
    <Stack gap="sm">
      {value.map((row, index) => {
        const media: MediaImageRef | null = row.uploadedFileId
          ? { uploadedFileId: row.uploadedFileId, url: row.uploadedFile?.fileUrl ?? null }
          : null;

        return (
          <Paper key={row.id ?? index} withBorder p="sm" radius="md">
            <Stack gap="sm">
              <Group gap="sm" wrap="nowrap" align="flex-end">
                <SelectField
                  style={{ flex: 1 }}
                  label={index === 0 ? t("artists:files.fields.fileKind.label", { defaultValue: "Type" }) : undefined}
                  data={kindOptions}
                  value={row.fileKind || ARTIST_FILE_KIND.OTHER}
                  onChange={(_name, v) => updateAt(index, { fileKind: v ?? ARTIST_FILE_KIND.OTHER })}
                  allowDeselect={false}
                  disabled={disabled}
                  size="md"
                />
                <Tooltip label={t("common:buttons.remove", { defaultValue: "Remove" })} withArrow position="top">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    mb={6}
                    onClick={() => removeAt(index)}
                    disabled={disabled}
                    aria-label={t("common:buttons.remove", { defaultValue: "Remove" })}
                  >
                    <AppIcon iconName="Trash" size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>

              <MediaImageField
                intent="content-image"
                allowUrl={false}
                value={media}
                onChange={(next) => handleMedia(index, next)}
                label={t("artists:files.fields.file.label", { defaultValue: "File" })}
                addLabel={t("artists:files.fields.file.add", { defaultValue: "Upload file" })}
                aspect={1.4}
                disabled={disabled}
              />

              {row.fileName && (
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {row.fileName}
                </Text>
              )}
            </Stack>
          </Paper>
        );
      })}

      {value.length === 0 && (
        <Box>
          <Text size="md" c="dimmed">
            {t("artists:files.empty", {
              defaultValue: "No documents yet — attach the contract, rider or tech spec.",
            })}
          </Text>
        </Box>
      )}

      {error && (
        <Text size="md" c="red">
          {error}
        </Text>
      )}

      <Group>
        <Button
          variant="light"
          size="md"
          onClick={addFile}
          disabled={disabled}
          leftSection={<AppIcon iconName="Plus" size={14} />}
        >
          {t("artists:files.add", { defaultValue: "Add document" })}
        </Button>
      </Group>
    </Stack>
  );
};

export default EventArtistFilesEditor;
